import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Column, DefaultTableFilterProps } from '@uni-space/uni-components';
import {
  Button,
  DownloadIcon,
  FlexContainer,
  Loader,
  ScanningIcon,
  SoundNotificationContext,
  useNotificationsV2,
} from '@uni-space/uni-components';
import { CompleteShipmentStatus, ShipmentDto, ShipmentStatus } from './shipment';
import { useShipmentStore } from './useShipmentStore';
import { completeShipment, exportShipment, getShipmentById } from '../api';
import { useMutation } from '@tanstack/react-query';

export const useShipmentSettingsTable = ({
  onRefetch,
  shipmentStatus,
}: {
  shipmentStatus: ShipmentStatus;
  onRefetch: () => void;
}) => {
  const { t } = useTranslation();
  const { handleChangeCurrentShipment, handleChangeBagData } = useShipmentStore();
  const [loadingIds, setLoadingIds] = useState<number[]>([]);
  const [loadingCompleteIds, setLoadingCompleteIds] = useState<number[]>([]);
  const [loadingExportIds, setLoadingExportIds] = useState<number[]>([]);
  const { createNotification } = useNotificationsV2();
  const { onSoundSuccess, onSoundError } = useContext(SoundNotificationContext);

  const handleStartScanning = async (shipment: ShipmentDto) => {
    try {
      setLoadingIds((prev) => [...prev, shipment.id]);
      const res = await getShipmentById(shipment.id);
      handleChangeCurrentShipment(shipment);
      handleChangeBagData(res.bags);
    } catch (error) {
    } finally {
      setLoadingIds((prev) => prev.filter((i) => i !== shipment.id));
    }
  };

  const handleExport = async (shipmentId: number) => {
    try {
      setLoadingExportIds((prev) => [...prev, shipmentId]);
      await exportShipment(shipmentId);
    } catch (error) {
      createNotification({
        type: 'error',
        message: {
          title: t('common.systemError.title'),
          desc: t('common.systemError.desc'),
        },
      });
      onSoundError?.();
    } finally {
      setLoadingExportIds((prev) => prev.filter((i) => i !== shipmentId));
    }
  };

  const { mutateAsync: completeShipmentMutate } = useMutation({
    mutationKey: ['complete-shipment'],
    mutationFn: completeShipment,
    onMutate: ({ shipmentId }) => {
      setLoadingCompleteIds((prev) => [...prev, shipmentId]);
    },
    onSettled: (_, __, { shipmentId }) => {
      setLoadingCompleteIds((prev) => prev.filter((i) => i != shipmentId));
    },
    onSuccess: (resp) => {
      switch (resp.status) {
        case CompleteShipmentStatus.Completed: {
          createNotification({
            type: 'success',
            message: {
              title: t('shipment.complete.success.title'),
              desc: t('shipment.complete.success.desc'),
            },
          });
          onSoundSuccess?.();
          onRefetch?.();
          return;
        }

        case CompleteShipmentStatus.ShipmentNotFound: {
          createNotification({
            type: 'error',
            message: {
              title: t('shipment.complete.shipmentNotFound.title'),
              desc: t('shipment.complete.shipmentNotFound.desc'),
            },
          });
          onSoundError?.();
          return;
        }

        case CompleteShipmentStatus.AlreadyCompleted: {
          createNotification({
            type: 'warning',
            message: {
              title: t('shipment.complete.alreadyCompleted.title'),
              desc: t('shipment.complete.alreadyCompleted.desc'),
            },
          });
          onSoundError?.();
          return;
        }

        case CompleteShipmentStatus.NoBagsInShipment: {
          createNotification({
            type: 'warning',
            message: {
              title: t('shipment.complete.noBags.title'),
              desc: t('shipment.complete.noBags.desc'),
            },
          });
          onSoundError?.();
          return;
        }

        case CompleteShipmentStatus.InternalError:
        default: {
          createNotification({
            type: 'error',
            message: {
              title: t('common.serverError.title'),
              desc: t('common.serverError.desc'),
            },
          });
          onSoundError?.();
          return;
        }
      }
    },

    onError: () => {
      createNotification({
        type: 'error',
        message: {
          title: t('common.systemError.title'),
          desc: t('common.systemError.desc'),
        },
      });
      onSoundError?.();
    },
  });

  const filters: DefaultTableFilterProps<ShipmentDto>[] = useMemo(
    () => [{
      type: 'search',
      id: 'bagNumbers',
      name: 'Track Number',
      label: t('tableHeader.bagNumber'),
      variant: 'primary',
    },
    {
      type: 'search',
      id: 'number',
      name: 'Shipment number',
      label: t('tableHeader.shipmentNumber'),
      variant: 'primary',
    },
    ],
    [t],
  );

  const columns: Column<ShipmentDto>[] = useMemo(
    () => [
      {
        header: t('tableHeader.number'),
        accessor: 'number',
        textAlign: 'center',
      },

      {
        header: t('tableHeader.vehicleNumber'),
        accessor: 'vehicleNumber',
        textAlign: 'center',
      },

      {
        header: t('tableHeader.assignBagsCount'),
        accessor: 'assingBagsCount',
        textAlign: 'center',
      },

      {
        header: t('isReturn'),
        accessor: 'isReturn',
        textAlign: 'center',
        minWidth: '120px',
        flex: 0.5,
        renderCell: (row) => (row.isReturn ? t('returnFlag.yes') : t('returnFlag.no')),
      },

      {
        header: t('tableHeader.actions'),
        minWidth: '340px',
        accessor: 'vehicleNumber',
        flex: 1,
        textAlign: 'center',
        renderCell: (row) => (
          <FlexContainer justify="center">
            {shipmentStatus == ShipmentStatus.Created && (
              <FlexContainer style={{ width: 'clamp(100px, 100%, 300px)' }} align="center" gap="8px">
                <Button
                  width="100%"
                  disabled={loadingIds.includes(row.id)}
                  loading={loadingIds.includes(row.id)}
                  onClick={() => handleStartScanning(row)}
                  type="secondary"
                >
                  <ScanningIcon width={26} height={14} />{' '}
                </Button>

                <Button
                  size="m"
                  disabled={loadingCompleteIds.includes(row.id)}
                  loading={loadingCompleteIds.includes(row.id)}
                  width="100%"
                  onClick={() => completeShipmentMutate({ shipmentId: row.id })}
                >
                  {t('complete')}
                </Button>
              </FlexContainer>
            )}
            {shipmentStatus == ShipmentStatus.Completed && (
              <Button
                disabled={loadingIds.includes(row.id)}
                loading={loadingIds.includes(row.id)}
                onClick={() => handleStartScanning(row)}
                type="secondary"
              >
                {t('details')}
              </Button>
            )}
          </FlexContainer>
        ),
      },

      {
        header: t('tableHeader.downloadShipment'),
        accessor: 'id',
        minWidth: '72px',
        textAlign: 'center',
        flex: 0.5,
        renderCell: (row) => (
          <FlexContainer justify="center" align="center">
            {loadingExportIds.includes(row.id) ? (
              <Loader size="s" />
            ) : (

              <DownloadIcon color='blue' onClick={() => handleExport(row.id)} width={18} height={18} />
            )}
          </FlexContainer>
        ),
      },
    ],
    [t, loadingIds, loadingCompleteIds, loadingExportIds, shipmentStatus],
  );

  return { filters, columns };
};
